import { AutocompleteField } from "@/features/FilterTable/AutocompleteField";
import {
  dateSchema,
  formatDateValueToISOString,
  parseToDateValue,
} from "@/features/FormCard/utils";
import { getAuthorized, postClientAction } from "@/lib/fetch";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";

const GenerateEDDExportsSchema = z.object({
  reportingDate: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional()
    .nullable()
    .describe("date"),
});

export type GenerateEDDExportsSchemaType = z.infer<
  typeof GenerateEDDExportsSchema
>;

const A7OPCVMGenerateEDDExports = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    reset,
  } = useForm<GenerateEDDExportsSchemaType>({
    mode: "onChange",
    defaultValues: {
      reportingDate: undefined,
    },
    resolver: zodResolver(
      GenerateEDDExportsSchema,
    ) as Resolver<GenerateEDDExportsSchemaType>,
  });

  const { data: dateOptions } = useQuery({
    queryKey: ["a7", "a7_opcvm_time_serie_files", "dates"],
    queryFn: async () => {
      return getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/a7/a7_opcvm_time_serie_files/dates`,
      )
        .then((response) => response.json())
        .then((res) =>
          res.map((date: string) => ({
            label: date,
            value: date,
          })),
        );
    },
  });

  const currentPage = useCurrentPageFromUrl();

  const generateEDDExportsMutation = useMutation({
    mutationFn: async (data: GenerateEDDExportsSchemaType) => {
      const formattedDate = formatDateValueToISOString(data.reportingDate);

      return await postClientAction(
        `${import.meta.env.VITE_BACKEND_URL}/a7/a7_opcvm_actions/edd_exports`,
        {
          reportingDate: formattedDate ?? "",
          prefix: "a7-opcvm",
          client: "a7-opcvm",
          action_button_name: t("edd_exports.action_name", {
            defaultValue: "Génération des exports EDD",
          }),
          action_execution_page: currentPage ?? "",
        },
      );
    },
    onSuccess: () => {
      toast.success(
        t("edd_exports.started", {
          defaultValue: "La génération des exports EDD a été lancée.",
        }),
      );
      setIsOpen(false);
      reset();
    },
    onError: () => {
      toast.error(
        t("edd_exports.error", {
          defaultValue:
            "Une erreur est survenue lors de la génération des exports EDD.",
        }),
      );
    },
  });

  const onSubmit = (data: GenerateEDDExportsSchemaType) => {
    generateEDDExportsMutation.mutate(data);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage:
              "linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)",
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("actions.generate_edd_exports", {
            defaultValue: "Générer exports EDD",
          })}
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
          <div>
            {dateOptions && (
              <AutocompleteField
                field={{
                  name: "reportingDate",
                  label: t("download_center.columns.reporting_date", {
                    defaultValue: "Date du rapport",
                  }),
                  type: "autocomplete",
                  options: dateOptions,
                }}
                setValue={setValue as any}
                watch={watch}
              />
            )}
          </div>
          <footer className="mt-4 w-full">
            <Button
              color="primary"
              radius="full"
              size="sm"
              type="submit"
              className="w-24"
              isDisabled={!isValid}
              isLoading={generateEDDExportsMutation.isPending}
            >
              {t("actions.generate", { defaultValue: "Générer" })}
            </Button>
          </footer>
        </form>
      </PopoverContent>
    </Popover>
  );
};

export default A7OPCVMGenerateEDDExports;
